import React, { useState, useMemo, useRef, useEffect } from 'react';
import { AgCharts } from 'ag-charts-react';
import { CHART_COLORS, getAgChartTheme, formatPercent } from '../../analytics/charts/chartTheme';
import { useTheme } from '../../../stores/ThemeContext';

const TAG_COLORS = {
  stop_loss_moved: '#ef4444',
  no_setup: '#f59e0b',
  no_plan_check: '#f59e0b',
  oversized_position: '#ef4444',
  fomo_entry: '#ef4444',
  revenge_entry: '#ef4444',
  early_exit: '#3b82f6',
  late_exit: '#f59e0b',
  added_to_loser: '#ef4444',
  traded_news: '#f59e0b',
  overtrading: '#ef4444',
  emotional_entry: '#ef4444',
  ignored_signal: '#f59e0b',
  break_session_rules: '#3b82f6',
};

const EMOTION_COLORS = {
  calm: '#4ade80',
  confident: '#34d399',
  neutral: '#fbbf24',
  anxious: '#f87171',
  frustrated: '#fb7185',
  excited: '#fb923c',
};

// Color palette for symbols - will cycle through these
const SYMBOL_COLOR_PALETTE = [
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#f59e0b', // amber
  '#10b981', // emerald
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
  '#84cc16', // lime
  '#a855f7', // purple
  '#14b8a6', // teal
  '#f43f5e', // rose
  '#eab308', // yellow
];

const RULE_TAG_LABELS = {
  stop_loss_moved: 'Stop Loss Moved',
  no_setup: 'No Setup / No Plan',
  no_plan_check: 'Skipped Plan Check',
  oversized_position: 'Oversized Position',
  fomo_entry: 'FOMO Entry',
  revenge_entry: 'Revenge Entry',
  early_exit: 'Early Exit',
  late_exit: 'Late Exit / Held Too Long',
  added_to_loser: 'Added to Losing Position',
  traded_news: 'Traded During News',
  overtrading: 'Overtrading',
  emotional_entry: 'Emotional Entry',
  ignored_signal: 'Ignored Exit Signal',
  break_session_rules: 'Broke Session Rules',
};

const TagEmotionPieChart = ({ data = {}, height = 350 }) => {
  const { isDark } = useTheme();
  // Left chart: filter by symbol, show tags/emotions
  const [leftMode, setLeftMode] = useState('tags');
  const [selectedSymbol, setSelectedSymbol] = useState('all');

  // Right chart: filter by tag/emotion, show symbols
  const [rightMode, setRightMode] = useState('tags');
  const [selectedTagEmotion, setSelectedTagEmotion] = useState('');

  const leftContainerRef = useRef(null);
  const rightContainerRef = useRef(null);
  const [leftDimensions, setLeftDimensions] = useState({ width: 0, height });
  const [rightDimensions, setRightDimensions] = useState({ width: 0, height });

  // ResizeObserver for left chart
  useEffect(() => {
    const container = leftContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (width > 0) {
          setLeftDimensions({ width, height });
        }
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [height]);

  // ResizeObserver for right chart
  useEffect(() => {
    const container = rightContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (width > 0) {
          setRightDimensions({ width, height });
        }
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [height]);

  // Extract available symbols
  const availableSymbols = useMemo(() => {
    const symbols = new Set();
    if (data.tags_by_symbol) {
      Object.keys(data.tags_by_symbol).forEach(s => symbols.add(s));
    }
    if (data.emotions_by_symbol) {
      Object.keys(data.emotions_by_symbol).forEach(s => symbols.add(s));
    }
    return ['all', ...Array.from(symbols).sort()];
  }, [data]);

  // Extract available tags and emotions for right chart
  const { availableTags, availableEmotions } = useMemo(() => {
    const tags = new Set();
    const emotions = new Set();

    if (data.tags_by_symbol) {
      Object.values(data.tags_by_symbol).forEach(symbolData => {
        Object.keys(symbolData).forEach(tag => tags.add(tag));
      });
    }

    if (data.emotions_by_symbol) {
      Object.values(data.emotions_by_symbol).forEach(symbolData => {
        Object.keys(symbolData).forEach(emotion => emotions.add(emotion));
      });
    }

    return {
      availableTags: Array.from(tags).sort(),
      availableEmotions: Array.from(emotions).sort(),
    };
  }, [data]);

  // Set default selected tag/emotion
  useEffect(() => {
    if (!selectedTagEmotion) {
      if (rightMode === 'tags' && availableTags.length > 0) {
        setSelectedTagEmotion(availableTags[0]);
      } else if (rightMode === 'emotions' && availableEmotions.length > 0) {
        setSelectedTagEmotion(availableEmotions[0]);
      }
    }
  }, [rightMode, availableTags, availableEmotions, selectedTagEmotion]);

  // LEFT CHART: Symbol → Tags/Emotions
  const leftChartData = useMemo(() => {
    const sourceData = leftMode === 'tags' ? data.tags_by_symbol : data.emotions_by_symbol;
    if (!sourceData) return [];

    const aggregated = {};

    if (selectedSymbol === 'all') {
      Object.values(sourceData).forEach(symbolData => {
        Object.entries(symbolData).forEach(([key, count]) => {
          aggregated[key] = (aggregated[key] || 0) + count;
        });
      });
    } else {
      const symbolData = sourceData[selectedSymbol];
      if (symbolData) {
        Object.assign(aggregated, symbolData);
      }
    }

    const total = Object.values(aggregated).reduce((sum, val) => sum + val, 0);
    if (total === 0) return [];

    const colorMap = leftMode === 'tags' ? TAG_COLORS : EMOTION_COLORS;

    return Object.entries(aggregated).map(([key, count]) => ({
      category: key,
      label: leftMode === 'tags'
        ? (RULE_TAG_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
        : key.charAt(0).toUpperCase() + key.slice(1),
      count,
      percentage: (count / total) * 100,
      fill: colorMap[key] || CHART_COLORS.neutral,
    })).sort((a, b) => b.count - a.count);
  }, [leftMode, selectedSymbol, data]);

  // RIGHT CHART: Tag/Emotion → Symbols
  const rightChartData = useMemo(() => {
    if (!selectedTagEmotion) return [];

    const sourceData = rightMode === 'tags' ? data.tags_by_symbol : data.emotions_by_symbol;
    if (!sourceData) return [];

    const symbolCounts = {};

    Object.entries(sourceData).forEach(([symbol, tagEmotionData]) => {
      const count = tagEmotionData[selectedTagEmotion];
      if (count) {
        symbolCounts[symbol] = count;
      }
    });

    const total = Object.values(symbolCounts).reduce((sum, val) => sum + val, 0);
    if (total === 0) return [];

    return Object.entries(symbolCounts).map(([symbol, count], index) => ({
      category: symbol,
      label: symbol,
      count,
      percentage: (count / total) * 100,
      fill: SYMBOL_COLOR_PALETTE[index % SYMBOL_COLOR_PALETTE.length],
    })).sort((a, b) => b.count - a.count);
  }, [rightMode, selectedTagEmotion, data]);

  // Chart options generator
  const createChartOptions = (chartData, dimensions, darkMode) => {
    const textColor = darkMode ? '#fafafa' : '#111827';
    return {
      theme: getAgChartTheme(darkMode),
      width: dimensions.width || undefined,
      height: dimensions.height,
      data: chartData,
      series: [
        {
          type: 'pie',
          angleKey: 'count',
          legendItemKey: 'label',
          fills: chartData.map(d => d.fill),
          strokes: chartData.map(d => d.fill),
          strokeWidth: 0,
          innerRadiusRatio: 0.6,
          calloutLabel: {
            enabled: true,
          },
          sectorLabel: {
            enabled: chartData.length <= 8,
            formatter: ({ datum }) => formatPercent(datum.percentage),
            color: textColor,
            fontWeight: 600,
          },
          tooltip: {
            renderer: ({ datum }) => ({
              title: datum.label,
              content: `Count: ${datum.count} (${formatPercent(datum.percentage)})`,
            }),
          },
          highlightStyle: {
            item: {
              fillOpacity: 0.8,
              strokeWidth: 2,
              stroke: textColor,
            },
          },
        },
      ],
      legend: {
        enabled: true,
        position: 'bottom',
        item: {
          marker: { shape: 'circle', size: 10 },
          label: { color: textColor, fontSize: 11 },
          paddingX: 12,
          paddingY: 6,
        },
      },
      background: { fill: CHART_COLORS.background },
    };
  };

  const leftOptions = useMemo(() => createChartOptions(leftChartData, leftDimensions, isDark), [leftChartData, leftDimensions, isDark]);
  const rightOptions = useMemo(() => createChartOptions(rightChartData, rightDimensions, isDark), [rightChartData, rightDimensions, isDark]);

  // Empty state
  if (!data.tags_by_symbol && !data.emotions_by_symbol) {
    return (
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <h3 className="text-sm font-semibold text-foreground mb-4">Tags & Emotions Distribution</h3>
        <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
          No data available. Journal trades to see tag and emotion distributions.
        </div>
      </div>
    );
  }

  const rightModeOptions = rightMode === 'tags' ? availableTags : availableEmotions;

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <h3 className="text-sm font-semibold text-foreground mb-4">Tags & Emotions Distribution</h3>

      {/* Two charts side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT CHART: Symbol → Tags/Emotions */}
        <div className="flex flex-col">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
            <h4 className="text-xs font-medium text-muted-foreground">By Symbol</h4>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              {/* Mode toggle */}
              <div className="flex items-center gap-1 bg-muted rounded-md p-0.5">
                <button
                  onClick={() => setLeftMode('tags')}
                  className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
                    leftMode === 'tags'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Tags
                </button>
                <button
                  onClick={() => setLeftMode('emotions')}
                  className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
                    leftMode === 'emotions'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Emotions
                </button>
              </div>
              {/* Symbol filter */}
              <select
                value={selectedSymbol}
                onChange={(e) => setSelectedSymbol(e.target.value)}
                className="px-2 py-1 text-[10px] bg-background border border-border rounded text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {availableSymbols.map(symbol => (
                  <option key={symbol} value={symbol}>
                    {symbol === 'all' ? 'All Symbols' : symbol}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div ref={leftContainerRef} className="w-full">
            {leftDimensions.width > 0 && leftChartData.length > 0 ? (
              <AgCharts options={leftOptions} />
            ) : (
              <div className="flex items-center justify-center h-64 text-muted-foreground text-xs">
                {leftDimensions.width === 0 ? 'Loading...' : 'No data for selected filters.'}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT CHART: Tag/Emotion → Symbols */}
        <div className="flex flex-col">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
            <h4 className="text-xs font-medium text-muted-foreground">By Tag/Emotion</h4>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              {/* Mode toggle */}
              <div className="flex items-center gap-1 bg-muted rounded-md p-0.5">
                <button
                  onClick={() => {
                    setRightMode('tags');
                    setSelectedTagEmotion(availableTags[0] || '');
                  }}
                  className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
                    rightMode === 'tags'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Tags
                </button>
                <button
                  onClick={() => {
                    setRightMode('emotions');
                    setSelectedTagEmotion(availableEmotions[0] || '');
                  }}
                  className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
                    rightMode === 'emotions'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Emotions
                </button>
              </div>
              {/* Tag/Emotion filter */}
              <select
                value={selectedTagEmotion}
                onChange={(e) => setSelectedTagEmotion(e.target.value)}
                className="px-2 py-1 text-[10px] bg-background border border-border rounded text-foreground focus:outline-none focus:ring-1 focus:ring-primary max-w-[160px]"
              >
                {rightModeOptions.map(item => (
                  <option key={item} value={item}>
                    {rightMode === 'tags'
                      ? (RULE_TAG_LABELS[item] || item.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
                      : item.charAt(0).toUpperCase() + item.slice(1)
                    }
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div ref={rightContainerRef} className="w-full">
            {rightDimensions.width > 0 && rightChartData.length > 0 ? (
              <AgCharts options={rightOptions} />
            ) : (
              <div className="flex items-center justify-center h-64 text-muted-foreground text-xs">
                {rightDimensions.width === 0 ? 'Loading...' : 'No data for selected filters.'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TagEmotionPieChart;
